import { EnhancedScrollArea } from "@components/enhanced-scroll-area";
import { useEffectPlease } from "@features/utils";
import { Button, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

// Demo data structure
const demoProfiles = {
  cassandre: {
    name: "Cassandre",
    originalPhoto: "/images/demo/cassandre/original.jpg",
    photos: Array.from(
      { length: 6 },
      (_, i) => `/images/demo/cassandre/${i + 1}.jpg`
    ),
    testimonial: "testimonies.cassandre_w",
  },
  claude: {
    name: "Claude",
    originalPhoto: "/images/demo/claude/original.jpg",
    photos: Array.from(
      { length: 5 },
      (_, i) => `/images/demo/claude/${i + 1}.jpg`
    ),
    testimonial: "testimonies.claude_b",
  },
  genia: {
    name: "Ashley",
    originalPhoto: "/images/demo/ashley/original.jpg",
    photos: Array.from(
      { length: 7 },
      (_, i) => `/images/demo/ashley/${i + 1}.jpg`
    ),
    testimonial: "testimonies.ashley_i",
  },
  gregoire: {
    name: "Grégoire",
    originalPhoto: "/images/demo/gregoire/original.jpg",
    photos: Array.from(
      { length: 7 },
      (_, i) => `/images/demo/gregoire/${i + 1}.jpg`
    ),
    testimonial: "testimonies.gregoire",
  },
  ilya: {
    name: "Ilya",
    originalPhoto: "/images/demo/ilya/original.jpg",
    photos: Array.from(
      { length: 6 },
      (_, i) => `/images/demo/ilya/${i + 1}.jpg`
    ),
    testimonial: "testimonies.ilya",
  },
  romaric: {
    name: "Romaric",
    originalPhoto: "/images/demo/romaric/original.jpg",
    photos: Array.from(
      { length: 10 },
      (_, i) => `/images/demo/romaric/${i + 1}.jpg`
    ),
    testimonial: "testimonies.romaric_mollard",
  },
  thibault: {
    name: "Thibault",
    originalPhoto: "/images/demo/thibault/original.jpg",
    photos: Array.from(
      { length: 5 },
      (_, i) => `/images/demo/thibault/${i + 1}.jpg`
    ),
    testimonial: "testimonies.thibault",
  },
};

let photoInterval: NodeJS.Timeout;
let profileInterval: NodeJS.Timeout;
let progressInterval: NodeJS.Timeout;

export const Examples = () => {
  const { t } = useTranslation();
  const [selectedProfile, setSelectedProfile] = useState<
    keyof typeof demoProfiles
  >(
    Object.keys(demoProfiles)[
      Math.floor(Math.random() * Object.keys(demoProfiles).length)
    ] as keyof typeof demoProfiles
  );
  const [selectedPhoto, setSelectedPhoto] = useState<string>("original");
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const currentProfile = demoProfiles[selectedProfile];
  const profileKeys = Object.keys(demoProfiles) as Array<
    keyof typeof demoProfiles
  >;

  const getCurrentImage = () => {
    if (selectedPhoto === "original") {
      return currentProfile.originalPhoto;
    }
    return currentProfile.photos[parseInt(selectedPhoto)];
  };

  const handleProfileChange = (profileKey: keyof typeof demoProfiles) => {
    setUserHasInteracted(true);
    setSelectedProfile(profileKey);
    setSelectedPhoto("original"); // Reset to original when changing profile
  };

  const handlePhotoChange = (photoIndex: string) => {
    setUserHasInteracted(true);
    setSelectedPhoto(photoIndex);
  };

  useEffectPlease(() => {
    if (userHasInteracted) return;

    const PHOTO_DURATION = 4000; // 2 seconds per photo
    const PROFILE_DURATION =
      currentProfile.photos.length * PHOTO_DURATION + 4000; // All photos + 4s for original

    // Photo cycling within current profile
    const startPhotoCycle = () => {
      let currentPhotoIndex = -1; // Start with original (-1)

      photoInterval = setInterval(() => {
        if (userHasInteracted) return; // Double check in case state changed

        currentPhotoIndex++;
        if (currentPhotoIndex >= currentProfile.photos.length) {
          currentPhotoIndex = -1; // Back to original
        }

        if (currentPhotoIndex === -1) {
          setSelectedPhoto("original");
        } else {
          setSelectedPhoto(currentPhotoIndex.toString());
        }
      }, PHOTO_DURATION);
    };

    // Profile cycling
    const startProfileCycle = () => {
      profileInterval = setInterval(() => {
        if (userHasInteracted) return; // Double check in case state changed

        setSelectedProfile((prev) => {
          const currentIndex = profileKeys.indexOf(prev);
          const nextIndex = (currentIndex + 1) % profileKeys.length;
          return profileKeys[nextIndex];
        });
        setSelectedPhoto("original"); // Reset to original when changing profile
      }, PROFILE_DURATION);
    };

    // Progress bar animation
    const startProgressAnimation = () => {
      let progress = 0;
      progressInterval = setInterval(() => {
        if (userHasInteracted) {
          const progressElement = document.getElementById(
            "examples_progress_bar"
          );
          if (progressElement) {
            progressElement.style.width = "0%";
          }
          return;
        }

        progress += 100 / (PHOTO_DURATION / 50); // Update every 50ms
        if (progress >= 100) {
          progress = 0;
        }

        const progressElement = document.getElementById(
          "examples_progress_bar"
        );
        if (progressElement) {
          progressElement.style.width = `${progress}%`;
        }
      }, 50);
    };

    // Start all animations
    startPhotoCycle();
    startProfileCycle();
    startProgressAnimation();

    // Cleanup
    return () => {
      if (photoInterval) clearInterval(photoInterval);
      if (profileInterval) clearInterval(profileInterval);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, []);

  // Reset progress bar when photo changes manually
  useEffect(() => {
    if (userHasInteracted) {
      clearInterval(progressInterval);
      clearInterval(photoInterval);
      clearInterval(profileInterval);
      const progressElement = document.getElementById("examples_progress_bar");
      if (progressElement) {
        progressElement.style.width = "0%";
      }
    }
  }, [selectedPhoto, userHasInteracted]);

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center space-x-2">
        {/* Original Photo Thumbnail */}
        <button
          onClick={() => handlePhotoChange("original")}
          className={twMerge(
            "flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden transition-all",
            selectedPhoto === "original"
              ? "border-white ring-2 ring-blue-500"
              : "border-white/50 hover:border-white"
          )}
        >
          <img
            src={currentProfile.originalPhoto}
            alt={t("reviews.before")}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src =
                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2RkZCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPj88L3RleHQ+PC9zdmc+";
            }}
          />
        </button>

        {/* Profile Tags */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(demoProfiles).map(([key, profile]) => (
            <Button
              key={key}
              variant={selectedProfile === key ? "solid" : "outline"}
              color={selectedProfile === key ? "blue" : "gray"}
              size="1"
              onClick={() =>
                handleProfileChange(key as keyof typeof demoProfiles)
              }
              className="transition-all"
            >
              {profile.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Main Photo Display */}
      <div className="relative bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden aspect-[1/1] w-full">
        <img
          src={getCurrentImage()}
          alt={`${currentProfile.name} - ${
            selectedPhoto === "original"
              ? t("reviews.before")
              : t("reviews.ai_generated")
          }`}
          className="w-full h-full object-contain bg-black"
          onError={(e) => {
            // Fallback to a placeholder if image fails to load
            e.currentTarget.src =
              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=";
          }}
        />

        {/* Photo Type Indicator */}
        <div className="absolute top-3 left-3">
          <div
            className={twMerge(
              "bg-slate-800/90 text-white rounded text-sm font-medium relative overflow-hidden",
              selectedPhoto === "original"
                ? "bg-slate-600/90"
                : "bg-green-600/90"
            )}
          >
            <div className="px-2 py-1">
              {selectedPhoto === "original"
                ? t("reviews.before")
                : t("reviews.ai_generated")}
            </div>
            {!userHasInteracted && (
              <div
                className="bottom-0 left-0 bg-white w-0 h-0.5 absolute"
                id="examples_progress_bar"
              ></div>
            )}
          </div>
        </div>

        {/* Photo Carousel at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent">
          <EnhancedScrollArea>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide p-5">
              {/* Generated Photos Thumbnails */}
              {currentProfile.photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => handlePhotoChange(index.toString())}
                  className={twMerge(
                    "flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden transition-all",
                    selectedPhoto === index.toString()
                      ? "border-white ring-2 ring-blue-500"
                      : "border-white/50 hover:border-white"
                  )}
                >
                  <img
                    src={photo}
                    alt={t("reviews.ai_generated")}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2RkZCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPj88L3RleHQ+PC9zdmc+";
                    }}
                  />
                </button>
              ))}
            </div>
          </EnhancedScrollArea>
        </div>
      </div>

      {/* Testimonial */}
      <div className="space-y-3 h-40">
        <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
          <Text size="2" className="italic leading-relaxed">
            "{t(`${currentProfile.testimonial}.testimony`)}"
          </Text>
        </div>
        <Text size="1" className="text-slate-500 font-medium">
          — {t(`${currentProfile.testimonial}.name`)}
        </Text>
      </div>
    </div>
  );
};
